import { useState, useMemo, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Check, X, Loader2 } from "lucide-react"
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { authApi } from '@/shared/services/auth'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// Contraseñas comunes a evitar (mismo criterio que ChangePasswordDialog)
const COMMON_PASSWORDS = [
  "password", "123456", "12345678", "qwerty", "abc123", "monkey", "1234567",
  "letmein", "trustno1", "dragon", "baseball", "iloveyou", "master", "sunshine",
  "ashley", "bailey", "shadow", "123123", "654321", "superman", "qazwsx",
  "michael", "football", "password1", "password123", "welcome", "jesus", "ninja",
  "mustang", "password2", "amanda", "jessica", "lovely", "12345", "123456789",
]

function validatePassword(password: string) {
  return {
    minLength: password.length >= 8,
    notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    notOnlyNumeric: !/^\d+$/.test(password),
  }
}

export default function ResetPasswordPage() {
  useDocumentTitle("Restablecer contraseña")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const uid = searchParams.get("uid") || ""
  const token = searchParams.get("token") || ""

  // Estados: validación inicial del enlace
  const [tokenState, setTokenState] = useState<"validating" | "valid" | "invalid">("validating")

  const [newPassword1, setNewPassword1] = useState("")
  const [newPassword2, setNewPassword2] = useState("")
  const [passwordMismatch, setPasswordMismatch] = useState(false)
  const [showNew1, setShowNew1] = useState(false)
  const [showNew2, setShowNew2] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState("")

  const passwordValidation = useMemo(() => validatePassword(newPassword1), [newPassword1])
  const isPasswordValid = newPassword1 === "" || (
    passwordValidation.minLength &&
    passwordValidation.notCommon &&
    passwordValidation.notOnlyNumeric
  )

  // Validar el enlace al montar
  useEffect(() => {
    if (!uid || !token) {
      setTokenState("invalid")
      return
    }
    let active = true
    authApi
      .validatePasswordReset({ uid, token })
      .then((response) => {
        if (active) setTokenState(response.success ? "valid" : "invalid")
      })
      .catch(() => {
        if (active) setTokenState("invalid")
      })
    return () => {
      active = false
    }
  }, [uid, token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!newPassword1) newErrors.new_password1 = "Ingresa la nueva contraseña"
    if (!newPassword2) newErrors.new_password2 = "Confirma la nueva contraseña"
    if (newPassword1 && newPassword2 && newPassword1 !== newPassword2) {
      newErrors.new_password2 = "Las contraseñas no coinciden"
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.confirmPasswordReset({
        uid,
        token,
        new_password1: newPassword1,
        new_password2: newPassword2,
      })

      if (response.success) {
        setSuccessMessage(response.message)
        setTimeout(() => navigate("/login"), 1800)
      } else if (response.errors) {
        setErrors(response.errors)
      } else {
        setErrors({ general: response.message })
        // Enlace inválido/expirado durante el submit
        if (/inválido|expirado/i.test(response.message)) {
          setTokenState("invalid")
        }
      }
    } catch {
      setErrors({ general: "Error de conexión. Inténtalo de nuevo." })
    } finally {
      setIsLoading(false)
    }
  }

  const renderBody = () => {
    if (tokenState === "validating") {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Validando enlace...</p>
        </div>
      )
    }

    if (tokenState === "invalid") {
      return (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <X className="h-12 w-12 text-destructive" />
          <p className="text-sm text-muted-foreground">
            El enlace es inválido o ha expirado. Solicita uno nuevo.
          </p>
          <Button asChild variant="outline" className="w-full h-12 text-base">
            <Link to="/forgot-password">Solicitar nuevo enlace</Link>
          </Button>
        </div>
      )
    }

    if (successMessage) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600" />
          <p className="text-sm text-muted-foreground">
            {successMessage}. Redirigiendo al inicio de sesión...
          </p>
        </div>
      )
    }

    return (
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="new_password1">Nueva contraseña</Label>
          <div className="relative">
            <Input
              id="new_password1"
              type={showNew1 ? "text" : "password"}
              value={newPassword1}
              onChange={(e) => {
                const value = e.target.value
                setNewPassword1(value)
                setPasswordMismatch(newPassword2 !== "" && value !== "" && value !== newPassword2)
              }}
              placeholder="Ingresa una nueva contraseña"
              className={errors.new_password1 || (!isPasswordValid && newPassword1) ? "border-destructive" : ""}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowNew1(!showNew1)}
            >
              {showNew1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {newPassword1 && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs">
                {passwordValidation.minLength ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
                <span className={passwordValidation.minLength ? "text-green-600" : "text-destructive"}>
                  Mínimo 8 caracteres
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {passwordValidation.notOnlyNumeric ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
                <span className={passwordValidation.notOnlyNumeric ? "text-green-600" : "text-destructive"}>
                  No puede ser solo números
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {passwordValidation.notCommon ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
                <span className={passwordValidation.notCommon ? "text-green-600" : "text-destructive"}>
                  No puede ser una contraseña común
                </span>
              </div>
            </div>
          )}
          {errors.new_password1 && (
            <p className="text-xs text-destructive">{errors.new_password1}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="new_password2">Confirmar nueva contraseña</Label>
          <div className="relative">
            <Input
              id="new_password2"
              type={showNew2 ? "text" : "password"}
              value={newPassword2}
              onChange={(e) => {
                const value = e.target.value
                setNewPassword2(value)
                setPasswordMismatch(newPassword1 !== "" && value !== "" && newPassword1 !== value)
              }}
              placeholder="Confirma tu nueva contraseña"
              className={errors.new_password2 || passwordMismatch ? "border-destructive" : ""}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowNew2(!showNew2)}
            >
              {showNew2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {passwordMismatch && !errors.new_password2 && (
            <p className="text-xs text-destructive">Las contraseñas no coinciden</p>
          )}
          {errors.new_password2 && (
            <p className="text-xs text-destructive">{errors.new_password2}</p>
          )}
        </div>

        {errors.general && (
          <div className="rounded-md bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{errors.general}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={isLoading || passwordMismatch || !newPassword1 || !newPassword2 || !isPasswordValid}
          className="w-full h-12 text-base text-white shadow-md bg-[#1e1e1e] hover:bg-[#2a2a2a] border-[#1e1e1e]"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Cambiar contraseña
        </Button>

        <Link
          to="/login"
          className="text-sm text-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 inline h-4 w-4" />
          Volver al inicio de sesión
        </Link>
      </form>
    )
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 bg-[#121212]">
      <div className="relative z-10 flex w-full max-w-md flex-col gap-6">
        <Card className="shadow-lg border-0">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Restablecer contraseña</CardTitle>
            <CardDescription className="text-base">
              Crea una nueva contraseña para tu cuenta
            </CardDescription>
          </CardHeader>
          <CardContent>{renderBody()}</CardContent>
        </Card>
      </div>
    </div>
  )
}
